import { useEffect, useState, type FormEvent } from 'react';

import { api } from '../../services/api/client';
import {
  disablePushSubscription,
  enablePushSubscription,
  synchronizeExistingPushSubscription,
} from '../../services/pwa/push-subscription';
import { PushDebugPanel } from './PushDebugPanel';
import { FeatureRequestForm } from './FeatureRequestForm';

interface SettingsPanelProps {
  profile: ProfileSettings;
  groupId: string;
  groupName?: string | null;
  showWrappedButton?: boolean;
  onOpenWrapped?: () => void;
  onProfileUpdated: (profile: ProfileSettings) => void;
  onAnnouncement: (message: string) => void;
}

interface ProfileSettings {
  weightKg: number;
  heightCm?: number | null | undefined;
  notificationsEnabled?: boolean | undefined;
}

export function SettingsPanel({
  profile,
  groupId,
  groupName,
  showWrappedButton = false,
  onOpenWrapped = () => undefined,
  onProfileUpdated,
  onAnnouncement,
}: SettingsPanelProps) {
  const [weight, setWeight] = useState(String(profile.weightKg));
  const [height, setHeight] = useState(String(profile.heightCm ?? 175));
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    profile.notificationsEnabled === true,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (profile.notificationsEnabled === true) {
      return;
    }
    let active = true;
    void synchronizeExistingPushSubscription((subscription) =>
      api.savePushSubscription(subscription),
    )
      .then((repaired) => {
        if (!active || !repaired) return;
        const updated = { ...profile, notificationsEnabled: true };
        setNotificationsEnabled(true);
        onProfileUpdated(updated);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [onProfileUpdated, profile]);

  async function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const weightKg = Number(weight.replace(',', '.'));
      const heightCm = Number(height.replace(',', '.'));
      if (!Number.isFinite(weightKg) || weightKg < 30 || weightKg > 250) {
        throw new Error('Das Gewicht muss zwischen 30 und 250 kg liegen.');
      }
      if (!Number.isFinite(heightCm) || heightCm < 120 || heightCm > 230) {
        throw new Error('Die Körpergröße muss zwischen 120 und 230 cm liegen.');
      }
      const updated = await api.updateProfile({ weightKg, heightCm });
      onProfileUpdated(updated);
      onAnnouncement('Persönliche Einstellungen gespeichert.');
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : 'Die Einstellungen konnten nicht gespeichert werden.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function toggleNotifications(nextEnabled: boolean) {
    setBusy(true);
    setError('');
    try {
      if (nextEnabled) {
        await enablePushSubscription({
          getPublicKey: () => api.pushPublicKey(),
          save: (subscription) => api.savePushSubscription(subscription),
        });
        const updated = await api.updateProfile({ notificationsEnabled: true });
        onProfileUpdated(updated);
        setNotificationsEnabled(true);
        onAnnouncement('Benachrichtigungen sind aktiviert.');
      } else {
        await disablePushSubscription((endpoint) => api.removePushSubscription(endpoint));
        const updated = await api.updateProfile({ notificationsEnabled: false });
        onProfileUpdated(updated);
        setNotificationsEnabled(false);
        onAnnouncement('Benachrichtigungen sind deaktiviert.');
      }
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : 'Die Benachrichtigungseinstellung konnte nicht geändert werden.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="personal-settings" aria-labelledby="personal-settings-title">
      <div className="settings-heading">
        <div>
          <p className="step-label">Persönlich</p>
          <h2 id="personal-settings-title">Meine Einstellungen</h2>
        </div>
      </div>
      <form onSubmit={(event) => void saveProfile(event)}>
        <div className="settings-fields">
          <label>
            Körpergewicht in kg
            <input
              type="number"
              min={30}
              max={250}
              step={0.1}
              inputMode="decimal"
              value={weight}
              onChange={(event) => setWeight(event.target.value)}
            />
          </label>
          <label>
            Körpergröße in cm
            <input
              type="number"
              min={120}
              max={230}
              step={0.1}
              inputMode="decimal"
              value={height}
              onChange={(event) => setHeight(event.target.value)}
            />
          </label>
        </div>
        <button className="button button--secondary settings-save" type="submit" disabled={busy}>
          {busy ? 'Wird gespeichert …' : 'Körperdaten speichern'}
        </button>
      </form>
      {showWrappedButton ? (
        <button
          className="button button--secondary settings-save"
          type="button"
          onClick={onOpenWrapped}
        >
          LBC Wrapped erneut anzeigen
        </button>
      ) : null}
      <label className="settings-toggle">
        <span>
          <strong>Benachrichtigungen</strong>
          <small>Wenn jemand in einer deiner Gruppen ein Getränk einträgt.</small>
        </span>
        <input
          type="checkbox"
          role="switch"
          checked={notificationsEnabled}
          disabled={busy}
          onChange={(event) => void toggleNotifications(event.target.checked)}
        />
      </label>
      <FeatureRequestForm groupId={groupId} onAnnouncement={onAnnouncement} />
      {error === '' ? null : (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {groupName === 'Jakob-Debug' ? (
        <PushDebugPanel serverNotificationsEnabled={notificationsEnabled} />
      ) : null}
    </section>
  );
}
